import {
    Card,
    DATE_FORMAT,
    DATE_WITHOUT_YEAR_FORMAT,
    IReportEntry,
    Legend,
    Segmented,
    SERVER_DATE_FORMAT,
    Switch,
} from '@localina/core';
import { Box } from '@mui/material';
import { AxisConfig, barElementClasses, BarChart, chartsGridClasses, HighlightScope, LineChart } from '@mui/x-charts';
import { DateTime } from 'luxon';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REPORTING_CHART_CHROME, REPORTING_CHARTS_COLORS } from '../../utils';

interface IChartsProps {
    graphicData: IReportEntry[];
    invalidIsVisible: boolean;
    validSegmentsAreVisible: boolean;
    onInvalidIsVisibleChange: (value: boolean) => void;
    onValidSegmentsAreVisibleChange: (value: boolean) => void;
}

export const Charts = forwardRef((props: IChartsProps, ref) => {
    const { t } = useTranslation();

    const series = useReportSeries(props.graphicData, props.invalidIsVisible, props.validSegmentsAreVisible);

    const chartTypes: { label: string; value: 'bar' | 'line' }[] = [
        { label: t('reports.barChartTab'), value: 'bar' },
        { label: t('reports.lineChartTab'), value: 'line' },
    ];
    const [activeTab, setActiveTab] = useState(chartTypes[0].value);
    return (
        <Card
            title={t('reports.sectionTitle.trend')}
            actions={
                <div className="reports-chart-toggles">
                    <Legend items={series.map(({ label, color }) => ({ label, color }))} />
                    <Switch
                        label={t('reports.displayNoShow')}
                        checked={props.invalidIsVisible}
                        onChange={props.onInvalidIsVisibleChange}
                    />
                    <Switch
                        label={t('reports.splitInSegments')}
                        checked={props.validSegmentsAreVisible}
                        onChange={props.onValidSegmentsAreVisibleChange}
                    />
                    <Segmented value={activeTab} options={chartTypes} onChange={setActiveTab} />
                </div>
            }
        >
            <Box sx={{ width: '100%' }} ref={ref}>
                <div className={'chart bar-chart'}>
                    <BarAndLineCharts data={props.graphicData} series={series} activeTab={activeTab} />
                </div>
            </Box>
        </Card>
    );
});

interface IReportSeries {
    label: string;
    data: number[];
    color: string;
    stack?: string;
    highlightScope?: HighlightScope;
}

const useReportSeries = (
    data: IReportEntry[],
    invalidIsVisible: boolean,
    validSegmentsAreVisible: boolean,
): IReportSeries[] => {
    const { t } = useTranslation();

    const valid: IReportSeries[] = !validSegmentsAreVisible
        ? [
              {
                  label: t('reports.legend.valid'),
                  data: data.map((entry) => entry.countWalkins + entry.countManual + entry.countOnline),
                  color: REPORTING_CHARTS_COLORS.valid,
                  stack: 'valid',
              },
          ]
        : [
              {
                  label: t('reports.legend.walkins'),
                  data: data.map((entry) => entry.countWalkins),
                  color: REPORTING_CHARTS_COLORS.walkin,
                  stack: 'valid',
              },
              {
                  label: t('reports.legend.manual'),
                  data: data.map((entry) => entry.countManual),
                  color: REPORTING_CHARTS_COLORS.manual,
                  stack: 'valid',
              },
              {
                  label: t('reports.legend.online'),
                  data: data.map((entry) => entry.countOnline),
                  color: REPORTING_CHARTS_COLORS.online,
                  stack: 'valid',
              },
          ];

    const invalid: IReportSeries = {
        label: t('reports.legend.noShow'),
        data: data.map((entry) => entry.countNoShow),
        color: REPORTING_CHARTS_COLORS.invalid,

        stack: 'valid',
        highlightScope: { faded: 'global', highlighted: 'item' },
    };

    return invalidIsVisible ? [...valid, invalid] : valid;
};

const X_AXIS_ID = 'reports-x';
const Y_AXIS_ID = 'reports-y';

interface IBarAndLineChartsProps {
    data: IReportEntry[];
    series: IReportSeries[];
    activeTab: 'bar' | 'line';
}

const BarAndLineCharts = (props: IBarAndLineChartsProps) => {
    const series = props.series;

    const currentYear = DateTime.now().year;
    const xAxisLabels = props.data.map((data) => {
        const date = DateTime.fromFormat(data.date, SERVER_DATE_FORMAT);
        return date.year === currentYear ? date.toFormat(DATE_WITHOUT_YEAR_FORMAT) : date.toFormat(DATE_FORMAT);
    });

    const axisTickStyle = {
        fill: REPORTING_CHART_CHROME.tickLabel,
        fontSize: REPORTING_CHART_CHROME.tickFontSize,
    };

    const bareAxis = (axisId: string) => ({
        axisId,
        disableLine: true,
        disableTicks: true,
        tickLabelStyle: axisTickStyle,
    });

    const barXAxis: AxisConfig<'band'> = {
        id: X_AXIS_ID,
        scaleType: 'band',
        data: xAxisLabels,
        categoryGapRatio: REPORTING_CHART_CHROME.categoryGapRatio,
        barGapRatio: 0,
    };

    const chartSx = {
        [`& .${chartsGridClasses.root} .${chartsGridClasses.line}`]: {
            stroke: REPORTING_CHART_CHROME.gridLine,
        },
        [`& .${barElementClasses.root}`]: { rx: REPORTING_CHART_CHROME.barRadius },
    };

    return (
        <>
            {props.activeTab === 'bar' && (
                <BarChart
                    height={300}
                    series={series}
                    xAxis={[barXAxis]}
                    yAxis={[{ id: Y_AXIS_ID }]}
                    bottomAxis={bareAxis(X_AXIS_ID)}
                    leftAxis={bareAxis(Y_AXIS_ID)}
                    grid={{ horizontal: true }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={chartSx}
                />
            )}
            {props.activeTab === 'line' && (
                <LineChart
                    height={300}
                    series={series.map((s) => ({
                        ...s,
                        stack: undefined,
                        showMark: false,

                        curve: 'linear' as const,
                        highlightScope: {
                            faded: 'global',
                            highlighted: 'series',
                        },
                    }))}
                    xAxis={[{ id: X_AXIS_ID, scaleType: 'point', data: xAxisLabels }]}
                    yAxis={[{ id: Y_AXIS_ID }]}
                    bottomAxis={bareAxis(X_AXIS_ID)}
                    leftAxis={bareAxis(Y_AXIS_ID)}
                    grid={{ horizontal: true }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={chartSx}
                />
            )}
        </>
    );
};

export { BarAndLineCharts };
